import type { AisClass } from '../types/scenario';
import type { InterpolatedPoint } from './interpolation';
import type { AisMessageType, AisPosition } from '../types/generated-data';
import { applyWaypointAnomaly } from './anomaly';

// ---------------------------------------------------------------------------
// AIS reporting interval rules (ITU-R M.1371-5)
// ---------------------------------------------------------------------------

/**
 * Class A position report intervals (seconds).
 * "Changing course" = ROT > ±5 deg/min (simplified threshold).
 */
function classAIntervalS(
  sogKn: number,
  isTurning: boolean,
  navStatus: string | null,
): number {
  // At anchor or moored
  if (navStatus === 'At anchor' || navStatus === 'Moored') return 180;

  if (sogKn > 23) return isTurning ? 2 : 3;
  if (sogKn > 14) return isTurning ? 2 : 6;
  if (sogKn > 0) return isTurning ? 3.33 : 10;
  // SOG === 0 but not anchored/moored
  return 180;
}

/** Class B position report intervals (seconds). */
function classBIntervalS(sogKn: number): number {
  return sogKn > 2 ? 30 : 180;
}

/** Turning threshold: |ROT| > 5 deg/min. */
const TURNING_THRESHOLD = 5;

/** Static/voyage report interval (~6 minutes). */
const STATIC_INTERVAL_S = 360;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface AisSamplerOptions {
  aisClass: AisClass;
  /** Duration of the scenario in seconds. */
  durationS: number;
  /** Absolute start time in milliseconds. */
  startMs: number;
  /** Track-level source metadata for building static reports. */
  source: {
    mmsi: string | null;
    imo: number | null;
    name: string;
    callSign: string | null;
    shipType: string | null;
    flag: string | null;
    width_m: number | null;
    length_m: number | null;
    draught_m: number | null;
    destination: string | null;
    eta: string | null;
  };
}

/**
 * Thin a high-resolution array of InterpolatedPoints down to realistic AIS
 * reporting intervals and interleave static/voyage reports.
 *
 * Returns AisPosition objects sorted by timestamp.
 */
export function aisRealisticSample(
  points: InterpolatedPoint[],
  options: AisSamplerOptions,
): AisPosition[] {
  if (points.length < 2) return [];

  const { aisClass, durationS, startMs, source } = options;
  const rangeMs = durationS * 1000;
  const positions: AisPosition[] = [];

  const positionMsgType: AisMessageType = aisClass === 'A' ? 1 : 18;
  const staticMsgType: AisMessageType = aisClass === 'A' ? 5 : 24;

  // Track when the next reports are due (in fractional time 0..1)
  let nextPositionFrac = 0;
  let nextStaticFrac = 0;

  // Always emit first point
  positions.push(
    buildPositionReport(
      points[0],
      positionMsgType,
      startMs,
      rangeMs,
      source,
      aisClass,
    ),
  );
  positions.push(
    buildStaticReport(
      points[0],
      staticMsgType,
      startMs,
      rangeMs,
      source,
      aisClass,
    ),
  );

  // Advance past first point
  const firstIntervalS = getIntervalS(points[0], aisClass);
  nextPositionFrac = firstIntervalS / durationS;
  nextStaticFrac = STATIC_INTERVAL_S / durationS;

  // Walk through high-resolution points and emit at realistic intervals
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    const frac = point.fraction;

    // Emit static/voyage report if due
    if (frac >= nextStaticFrac) {
      positions.push(
        buildStaticReport(
          point,
          staticMsgType,
          startMs,
          rangeMs,
          source,
          aisClass,
        ),
      );
      nextStaticFrac = frac + STATIC_INTERVAL_S / durationS;
    }

    // Emit position report if due
    if (frac >= nextPositionFrac) {
      positions.push(
        buildPositionReport(
          point,
          positionMsgType,
          startMs,
          rangeMs,
          source,
          aisClass,
        ),
      );
      const intervalS = getIntervalS(point, aisClass);
      nextPositionFrac = frac + intervalS / durationS;
    }
  }

  // Always include last point as a position report
  const lastPoint = points[points.length - 1];
  const lastPos = positions[positions.length - 1];
  const lastTimestamp = new Date(
    startMs + lastPoint.fraction * rangeMs,
  ).toISOString();
  if (lastPos.timestamp !== lastTimestamp) {
    positions.push(
      buildPositionReport(
        lastPoint,
        positionMsgType,
        startMs,
        rangeMs,
        source,
        aisClass,
      ),
    );
  }

  // Sort by timestamp (position and static reports are interleaved)
  positions.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  return positions;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getIntervalS(point: InterpolatedPoint, aisClass: AisClass): number {
  if (aisClass === 'B') return classBIntervalS(point.sog_kn);
  const isTurning = Math.abs(point.rot_deg_min) > TURNING_THRESHOLD;
  return classAIntervalS(point.sog_kn, isTurning, point.nav_status ?? null);
}

function buildPositionReport(
  point: InterpolatedPoint,
  msgType: AisMessageType,
  startMs: number,
  rangeMs: number,
  source: AisSamplerOptions['source'],
  aisClass: AisClass,
): AisPosition {
  const timestampMs = startMs + point.fraction * rangeMs;

  const built: AisPosition = {
    message_type: msgType,
    // Identity — position reports carry minimal identity
    mmsi: point.mmsi ?? source.mmsi,
    imo: null, // Not in position reports
    name: point.name ?? source.name,
    call_sign: null, // Not in position reports
    ship_type: null, // Not in position reports
    ais_type: `Class ${point.aisClass ?? aisClass}`,
    flag: null, // Not in position reports
    width_m: null,
    length_m: null,
    draught_m: null,
    // Kinematics
    timestamp: new Date(timestampMs).toISOString(),
    latitude: Number(point.latitude.toFixed(6)),
    longitude: Number(point.longitude.toFixed(6)),
    speed_over_ground_kn: Number(point.sog_kn.toFixed(1)),
    heading_deg: Number((point.heading_deg ?? point.bearing_deg).toFixed(1)),
    course_over_ground_deg: Number(point.bearing_deg.toFixed(1)),
    rate_of_turn_deg_min:
      aisClass === 'A' ? Number(point.rot_deg_min.toFixed(1)) : 0,
    // Operational
    nav_status: aisClass === 'A' ? (point.nav_status ?? null) : null,
    destination: null, // Not in position reports
    eta: null, // Not in position reports
    anomaly: null,
  };
  return applyWaypointAnomaly(built, point.waypointType);
}

function buildStaticReport(
  point: InterpolatedPoint,
  msgType: AisMessageType,
  startMs: number,
  rangeMs: number,
  source: AisSamplerOptions['source'],
  aisClass: AisClass,
): AisPosition {
  const timestampMs = startMs + point.fraction * rangeMs;

  const built: AisPosition = {
    message_type: msgType,
    // Identity — static reports carry full identity
    mmsi: point.mmsi ?? source.mmsi,
    imo: aisClass === 'A' ? (point.imo ?? source.imo ?? null) : null,
    name: point.name ?? source.name,
    call_sign: point.callSign ?? source.callSign ?? null,
    ship_type: point.shipType ?? source.shipType ?? null,
    ais_type: `Class ${point.aisClass ?? aisClass}`,
    flag: point.flag ?? source.flag ?? null,
    // Dimensions
    width_m: point.width_m ?? source.width_m ?? null,
    length_m: point.length_m ?? source.length_m ?? null,
    draught_m:
      aisClass === 'A' ? (point.draught_m ?? source.draught_m ?? null) : null,
    // Kinematics — static reports include position for reference
    timestamp: new Date(timestampMs).toISOString(),
    latitude: Number(point.latitude.toFixed(6)),
    longitude: Number(point.longitude.toFixed(6)),
    speed_over_ground_kn: 0, // Not in static reports
    heading_deg: 0,
    course_over_ground_deg: 0,
    rate_of_turn_deg_min: 0,
    // Operational
    nav_status: null, // Not in static reports
    destination:
      aisClass === 'A'
        ? (point.destination ?? source.destination ?? null)
        : null,
    eta: aisClass === 'A' ? (point.eta ?? source.eta ?? null) : null,
    anomaly: null,
  };
  return applyWaypointAnomaly(built, point.waypointType);
}
